/**
 * useMutationCommand — Effect-native mutation 的 React 消费面。
 *
 * 取代 useMutationRunner 的 Promise 生命周期：
 * - 状态：AsyncResult（Initial/Waiting/Success/Failure），typed error 保留
 * - 提交：execute(Effect) / executePromise(Effect)（窄 Promise 终端，表单库适配）
 * - 重复提交：drop（返回 Dropped），绝不悬挂
 * - 失效：reactivityKeys 成功后统一失效（跨页仍靠 staleTime 兜底）
 * - 错误 toast：集中于此适配层一次，业务 hook 不再各自 try/catch
 */

import { useCallback, useRef, useSyncExternalStore } from "react";
import { useAtomSet } from "@effect/atom-react";
import { Cause, Effect, Scope } from "effect";
import * as AsyncResult from "effect/unstable/reactivity/AsyncResult";
import { reactivityInvalidateAtom } from "@crm/shared/query-atom";
import {
  makeMutationCommand,
  readMutationState,
  subscribeMutationState,
  type MutationCommand,
} from "@crm/shared/query-atom/mutation";
import { reportMutationError } from "./use-mutation-error";

export type UseMutationCommandOptions = {
  /** 失败 toast 文案 */
  readonly message: string;
  /** 成功后统一失效的声明式键（同屏订阅者刷新） */
  readonly reactivityKeys?: ReadonlyArray<unknown>;
};

export type MutationCommandHandle<A, E> = {
  readonly state: AsyncResult.AsyncResult<A, E>;
  readonly isPending: boolean;
  readonly execute: (program: Effect.Effect<A, E, Scope.Scope>) => void;
  /** 窄 Promise 终端：仅供确需 await 的表单库边界使用 */
  readonly executePromise: (program: Effect.Effect<A, E, Scope.Scope>) => Promise<A>;
  readonly reset: () => void;
};

export function useMutationCommand<A, E>(
  options: UseMutationCommandOptions,
  command?: MutationCommand<A, E>,
): MutationCommandHandle<A, E> {
  const owned = useRef<MutationCommand<A, E> | undefined>(undefined);
  if (command === undefined && owned.current === undefined) {
    owned.current = makeMutationCommand<A, E>();
  }
  const active = command ?? owned.current!;

  const subscribe = useCallback(
    (listener: () => void) => subscribeMutationState(active, listener),
    [active],
  );
  const getSnapshot = useCallback(() => readMutationState(active), [active]);
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const invalidateKeys = useAtomSet(reactivityInvalidateAtom);

  const submit = useCallback(
    (program: Effect.Effect<A, E, Scope.Scope>): Promise<A> | undefined => {
      let reported = false;
      const tracked = Effect.onExit(program, (exit) =>
        Effect.sync(() => {
          if (exit._tag === "Success") {
            if (options.reactivityKeys) invalidateKeys(options.reactivityKeys);
          } else if (!reported) {
            reported = true;
            // extractErrorDetail expects the typed failure, not an AsyncResult wrapper.
            void reportMutationError(Cause.squash(exit.cause), options.message);
          }
        }),
      );
      const outcome = active.execute(tracked);
      return outcome._tag === "Dropped" ? undefined : outcome.completion;
    },
    [active, invalidateKeys, options.reactivityKeys, options.message],
  );

  return {
    state,
    isPending: AsyncResult.isWaiting(state),
    execute: (program) => {
      void submit(program)?.catch(() => undefined);
    },
    executePromise: (program) => submit(program) ?? Promise.reject(new MutationDroppedError()),
    reset: () => {
      active.reset();
    },
  };
}

/** 重复提交被 drop 时的窄终端错误（typed，绝不悬挂） */
export class MutationDroppedError extends Error {
  readonly _tag = "MutationDropped" as const;
  constructor() {
    super("mutation dropped: another submission is still running");
  }
}
